import React, { useState, useEffect } from 'react';
import { Container, IconButton, Typography, MenuItem, Select, FormControl, InputLabel, Box } from '@mui/material';
import { useMedicalScribeData } from './MedicalScribeData';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import SearchBar from '../../components/Search.js';
import Calendar from 'react-calendar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../loader.js';
import '../../Calendar.css'; 
import MiniDrawer from '../../components/MiniDrawer.js';
import { useTheme } from '@mui/material/styles';
import useAlert from '../../alert/useAlert.js';
import AlertDialog from '../../Dialog.js';
import {
  getVitalFlagsFromScan,
  getWellnessFlagsFromScan,
  getStressLabel as getStressLabelFromIndex,
} from './Dashboard/mindvaultUtils';
import { supabase } from '../../supabaseClient.js';
import { CLIENT_CHAT_SESSION_TYPES } from '../../utils/chatSessionsQuery.js';
import { isEntitled } from '../../utils/scribeConnection.js';
import { clientTherapyStats } from '../../services/clientSessionStats';
import { useClientTherapyHistory } from '../../hooks/useClientTherapyHistory';
import ClinicalView from './Views/ClinicalView';
import MentalViewMindVault from './Views/MentalViewMindvault.js';
import CorporateViewVitals from './Views/CorporateViewVitals.js';
import NewPatientForm from './Views/NewPatientForm.js';
import TourPatientDemo from '../../components/TourPatientDemo.js';
import { tourPatientScreens, getTourPatientScreen } from '../../components/tourPatientScreens';


const MedicalScribe = () => {
  const {
    patientList,
    consultationList,
    newPatient,
    setNewPatient,
    createPatient,
    userDetails,
    consultationLoading,
    teamMembersData,
    currentOrg,
    subscriptionActiveStatus,
    viewType,
    fetchCorporateVitalsRecords,
    currentUserRole,
  } = useMedicalScribeData();
  const [view, setView] = useState('Patients');
  const [sortOrder, setSortOrder] = useState('Newest');
  const [date, setDate] = useState(new Date()); 
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [sortedConsultations, setSortedConsultations] = useState([])
  const [sortedPatients, setSortedPatients] = useState([])
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [hkid, setHkid] = useState('');
  const [remarks, setRemarks] = useState('');
  const [preExistingConditions, setPreExistingConditions] = useState([]);
  const [status, setStatus] = useState('Active');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState('');
  const [emergencyContactPerson, setEmergencyContactPerson] = useState('');
  const [presentingConcerns, setPresentingConcerns] = useState('');
  const [diagnosis, setDiagnosis] = useState([]);
  const [countryCode, setCountryCode] = useState('852');
  const [email, setEmail] = useState('');
  const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });
  const { setAlert } = useAlert();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const tourDemoScreen = getTourPatientScreen(location.search);
  const isVisualTour = Boolean(tourDemoScreen);
  const demoPatients = [{
    id: 'tour-patient-alex-morgan',
    firstName: 'Alex',
    lastName: 'Morgan',
    name: 'Alex Morgan',
    gender: 'Female',
    age: 34,
    status: 'Active',
    phone_code: '+852',
    phoneNumber: '5555 0184',
    presenting_concerns: 'Sleep disruption and work-related stress',
    connection: 'Connected',
    sharedProfile: 'Shared',
    createdAt: new Date().toISOString(),
  }, {
    id: 'tour-patient-jamie-lee', firstName: 'Jamie', lastName: 'Lee', name: 'Jamie Lee', gender: 'Male', age: 42, status: 'Active', phone_code: '+852', phoneNumber: '5555 0142', presenting_concerns: 'Follow-up consultation', connection: 'Not connected', sharedProfile: 'Not shared', createdAt: new Date(Date.now() - 86400000).toISOString(),
  }, {
    id: 'tour-patient-sam-taylor', firstName: 'Sam', lastName: 'Taylor', name: 'Sam Taylor', gender: 'Female', age: 29, status: 'Inactive', phone_code: '+852', phoneNumber: '5555 0179', presenting_concerns: 'Profile pending review', connection: 'Connected', sharedProfile: 'Not shared', createdAt: new Date(Date.now() - 172800000).toISOString(),
  }];

  const [selectedStatusFilters, setSelectedStatusFilters] = useState(['All']);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [selectedGenderFilters, setSelectedGenderFilters] = useState(['All']);
  const [genderMenuAnchorEl, setGenderMenuAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [gender, setGender] = useState('Male');
  const [birth_date, setBirthDate] = useState(null);
  const [case_owner, setCaseOwner] = useState([]);
  const [mindvaultMap, setMindvaultMap] = useState({});
  // const userMode = userDetails?.mode || 'Corporates';
  // Tour mode deliberately renders the existing clinical workspace with one
  // in-memory patient. Nothing below is persisted or sent to Supabase.
  const userMode = isVisualTour ? 'Clinical' : 'Corporates';


  const [selectedCaseOwnerFilter, setSelectedCaseOwnerFilter] = useState('ALL');

  useEffect(() => {
    if (currentUserRole && currentUserRole !== 'Admin' && userDetails?.id) {
      setSelectedCaseOwnerFilter(userDetails.id);
    }
  }, [currentUserRole, userDetails?.id]);

  const [timeRange, setTimeRange] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');


  const [corporateMap, setCorporateMap] = useState({});
  const [corporateLoading, setCorporateLoading] = useState(false);
  const therapy = useClientTherapyHistory(supabase, {
    enabled: !isVisualTour && userMode === 'Corporates' && viewType === 'MindVault',
    teamId: currentOrg?.id, therapistId: userDetails?.id, patients: patientList,
  });

  const [corporateVitalsRecords, setCorporateVitalsRecords] = useState([]);
  const [corporateVitalsLoading, setCorporateVitalsLoading] = useState(false);

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  const dateRangeFromPreset = (preset, customFrom, customTo) => {
    const now = new Date();
    if (preset === 'custom' && customFrom && customTo) {
      return { start: startOfDay(customFrom), end: endOfDay(customTo) };
    }
    if (preset === 'month') {
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    if (preset === 'quarter') {
      const start = new Date();
      start.setDate(start.getDate() - 90);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return { start: startOfDay(start), end: endOfDay(now) };
  };

  const getStressLabel = (v) => {
    if (!v) return null;
    if (typeof v === 'string') {
      if (v.toLowerCase() === 'high') return 'High';
      if (v.toLowerCase() === 'moderate') return 'Moderate';
    }
    // Same 50 / 200 / 350 / 500 bands as vitals-chat (mindvaultUtils).
    return getStressLabelFromIndex(v);
  };

  const getRiskChipStyles = (level) => {
    switch ((level || '').toUpperCase()) {
      case 'HIGH':
        return { bg: '#FEE2E2', text: '#B91C1C', border: '#FCA5A5' };
      case 'MEDIUM':
        return { bg: '#FEF3C7', text: '#B45309', border: '#FCD34D' };
      case 'LOW':
        return { bg: '#F3F4F6', text: '#4B5563', border: '#D1D5DB' };
      default:
        return { bg: '#F3F4F6', text: '#4B5563', border: '#D1D5DB' };
    }
  };

  useEffect(() => {
    const normalizeSignals = (v) => {
      if (!v) return [];
      if (Array.isArray(v)) return v;
      if (typeof v === 'string') {
        try { return JSON.parse(v) || []; } catch { return v.split(',').map((x) => x.trim()).filter(Boolean); }
      }
      return [];
    };

    const loadCorporateVitalsRecords = async () => {
      if (userMode !== 'Corporates' || viewType !== 'Vitals' || !currentOrg?.id) {
        setCorporateVitalsRecords([]);
        setCorporateMap({});
        return;
      }

      setCorporateVitalsLoading(true);
      try {
        const { start, end } = dateRangeFromPreset(timeRange, customFrom, customTo);
        const { records, groupedByRecord } = await fetchCorporateVitalsRecords({
          teamId: currentOrg.id,
          startIso: start.toISOString(),
          endIso: end.toISOString(),
        });

        setCorporateVitalsRecords(records);

        const nextMap = {};
        records.forEach((r) => {
          const g = groupedByRecord[r.id] || { scans: [], chats: [], latestScan: null };
          const chatRiskSignals = { HIGH: new Set(), MEDIUM: new Set() };
          const chatRiskCounts = { HIGH: 0, MEDIUM: 0 };

          (g.chats || []).forEach((c) => {
            const level = (c.risk_level || '').toUpperCase();
            if (level === 'HIGH' || level === 'MEDIUM') {
              chatRiskCounts[level] += 1;
              normalizeSignals(c.risk_signals).forEach((s) => chatRiskSignals[level].add(s));
            }
          });

          nextMap[r.id] = {
            linked: (g.scans?.length || 0) > 0 || (g.chats?.length || 0) > 0,
            chatCount: g.chats?.length || 0,
            scanCount: g.scans?.length || 0,
            appointmentsCount: 0,
            vitalFlags: getVitalFlagsFromScan(g.latestScan),
            wellnessFlags: getWellnessFlagsFromScan(g.latestScan),
            stressLabel: getStressLabel(g.latestScan?.stress_index),
            chatRiskCounts,
            chatRiskSignals: {
              HIGH: Array.from(chatRiskSignals.HIGH),
              MEDIUM: Array.from(chatRiskSignals.MEDIUM),
            },
          };
        });

        setCorporateMap(nextMap);
      } catch (err) {
        console.error('Error loading corporate vitals records:', err);
        setCorporateVitalsRecords([]);
        setCorporateMap({});
      } finally {
        setCorporateVitalsLoading(false);
      }
    };

    loadCorporateVitalsRecords();
  }, [userMode, viewType, currentOrg?.id, timeRange, customFrom, customTo]);

  useEffect(() => {
    const loadCorporateStats = async () => {
      if (userMode !== 'Corporates' || viewType !== 'MindVault') {
        return;
      }

      if (!patientList?.length) {
        setCorporateMap({});
        return;
      }

      setCorporateLoading(true);
      try {
        const { start, end } = dateRangeFromPreset(timeRange, customFrom, customTo);

        // Connection rule lives server-side (get_scribe_connection_statuses):
        // connected = (paid booking OR active access code) AND client consent.
        // Scanning mindvault_accounts here and matching on email would only
        // see consent, surfacing data for clients nobody paid for.
        const emails = patientList
          .map((p) => (p.email ?? '').trim().toLowerCase())
          .filter(Boolean);
        const { data: statuses, error: statusErr } = await supabase.rpc(
          'get_scribe_connection_statuses',
          {
            p_therapist_user_id: userDetails?.id,
            p_client_emails: emails,
          }
        );
        if (statusErr) throw statusErr;

        const statusByEmail = new Map();
        (statuses || []).forEach((s) => statusByEmail.set(s.client_email, s));

        const matched = patientList.map((p) => {
          const emailKey = (p.email ?? '').trim().toLowerCase();
          const s = emailKey ? statusByEmail.get(emailKey) : null;
          return {
            patientId: p.id,
            // userId only when connected — everything downstream keys on it,
            // so an unconnected client's rows are never even fetched
            userId: s?.connection_status ? s.mindvault_account_id : null,
            hasMvAccount: !!s?.mindvault_account_id,
            connectionStatus: s?.connection_status || null,
            entitlement: s?.entitlement || null,
            // Paid for (booking, live code, or code awaiting settlement) but
            // the client hasn't shared: restricted access, not "not connected".
            awaitingConsent: !s?.connection_status && isEntitled(s?.entitlement),
          };
        });

        const matchedUserIds = matched.map((m) => m.userId).filter(Boolean);
        if (!matchedUserIds.length) {
          setCorporateMap(
            Object.fromEntries(matched.map((m) => [
              m.patientId,
              {
                linked: m.hasMvAccount,
                accessRestricted: m.hasMvAccount,
                connectionStatus: null,
                entitlement: m.entitlement,
                awaitingConsent: m.awaitingConsent,
              },
            ]))
          );
          return;
        }

        const [chatRes, scanRes] = await Promise.all([
          supabase
            .from('chat_sessions')
            .select('user_id, created_at, risk_signals, risk_level')
            .in('user_id', matchedUserIds)
            .eq('is_deleted', false)
            .or(CLIENT_CHAT_SESSION_TYPES)
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString()),
          supabase
            .from('scan_history')
            .select(
              'user_id, created_at, activity, sleep, equilibrium, metabolism, health, relaxation, heart_rate, sbp, dbp, oxygen_saturation, respiration_rate, stress_index, hr_variability'
            )
            .in('user_id', matchedUserIds)
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString()),
        ]);

        const chats = chatRes?.data || [];
        const scans = scanRes?.data || [];

        const chatCountByUser = {};
        const scanCountByUser = {};
        const chatRiskCountByUser = {};
        const chatRiskSignalsByUser = {};

        const normalizeSignals = (v) => {
          if (!v) return [];
          if (Array.isArray(v)) return v;
          if (typeof v === 'string') {
            try { return JSON.parse(v) || []; } catch { return v.split(',').map((x) => x.trim()).filter(Boolean); }
          }
          return [];
        };

        chats.forEach((c) => {
          chatCountByUser[c.user_id] = (chatCountByUser[c.user_id] || 0) + 1;

          const level = (c.risk_level || '').toUpperCase();
          const signals = normalizeSignals(c.risk_signals);

          if (level === 'HIGH' || level === 'MEDIUM') {
            if (!chatRiskCountByUser[c.user_id]) {
              chatRiskCountByUser[c.user_id] = { HIGH: 0, MEDIUM: 0 };
            }
            chatRiskCountByUser[c.user_id][level] += 1;

            if (!chatRiskSignalsByUser[c.user_id]) {
              chatRiskSignalsByUser[c.user_id] = { HIGH: new Set(), MEDIUM: new Set() };
            }
            signals.forEach((s) => chatRiskSignalsByUser[c.user_id][level].add(s));
          }
        });

        scans.forEach((s) => {
          scanCountByUser[s.user_id] = (scanCountByUser[s.user_id] || 0) + 1;
        });

        const latestScanByUser = {};
        scans
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .forEach((s) => {
            if (!latestScanByUser[s.user_id]) latestScanByUser[s.user_id] = s;
          });

        const map = {};
        matched.forEach((m) => {
          if (!m.userId) {
            // No connection: `linked` still reflects whether a MindVault
            // account exists (so the UI can offer to buy an access code),
            // but no data was fetched and none is exposed.
            map[m.patientId] = {
              linked: m.hasMvAccount,
              accessRestricted: m.hasMvAccount,
              connectionStatus: null,
              entitlement: m.entitlement,
              awaitingConsent: m.awaitingConsent,
            };
            return;
          }
          const scan = latestScanByUser[m.userId];
          const vitalFlags = getVitalFlagsFromScan(scan);
          const wellnessFlags = getWellnessFlagsFromScan(scan);
          const stressLabel = getStressLabel(scan?.stress_index);

          map[m.patientId] = {
            linked: true,
            accessRestricted: false,
            awaitingConsent: false,
            connectionStatus: m.connectionStatus,
            chatCount: chatCountByUser[m.userId] || 0,
            scanCount: scanCountByUser[m.userId] || 0,
            vitalFlags,
            wellnessFlags,
            stressLabel,
            chatRiskCounts: chatRiskCountByUser[m.userId] || { HIGH: 0, MEDIUM: 0 },
            chatRiskSignals: {
              HIGH: Array.from(chatRiskSignalsByUser[m.userId]?.HIGH || []),
              MEDIUM: Array.from(chatRiskSignalsByUser[m.userId]?.MEDIUM || []),
            },
          };
        });

        setCorporateMap(map);
      } catch (err) {
        console.error('Error loading corporate stats:', err);
        setCorporateMap({});
      } finally {
        setCorporateLoading(false);
      }
    };

    if (userMode === 'Corporates') loadCorporateStats();
  }, [userMode, patientList, timeRange, customFrom, customTo, currentOrg]);

  useEffect(() => {
    const loadMindvaultFlags = async () => {
      if (!patientList?.length) {
        setMindvaultMap({});
        return;
      }

      try {
        // Connection rule lives server-side (get_scribe_connection_statuses):
        // connected = (paid booking OR active access code) AND user consent.
        // No connection => no MindVault data surfaced, only whether a buyable
        // MV account exists for the email.
        const emails = patientList
          .map((p) => (p.email ?? '').trim().toLowerCase())
          .filter(Boolean);
        const { data: statuses, error } = await supabase.rpc(
          'get_scribe_connection_statuses',
          {
            p_therapist_user_id: userDetails?.id,
            p_client_emails: emails,
          }
        );

        if (error) {
          console.error('Error fetching scribe connection statuses:', error);
          setMindvaultMap({});
          return;
        }

        const statusByEmail = new Map();
        (statuses || []).forEach((s) => statusByEmail.set(s.client_email, s));

        const matched = patientList.map((p) => {
          const key = (p.email ?? '').trim().toLowerCase();
          const s = key ? statusByEmail.get(key) : null;
          return {
            patientId: p.id,
            // userId only set when connected — everything downstream keys on it
            userId: s?.connection_status ? s.mindvault_account_id : null,
            connectionStatus: s?.connection_status || null,
            hasMvAccount: !!s?.mindvault_account_id,
            entitlement: s?.entitlement || null,
            awaitingConsent: !s?.connection_status && isEntitled(s?.entitlement),
          };
        });

        const matchedUserIds = matched.map((m) => m.userId).filter(Boolean);
        if (!matchedUserIds.length) {
          setMindvaultMap(
            Object.fromEntries(
              matched.map((m) => [
                m.patientId,
                {
                  linked: false,
                  hasMvAccount: m.hasMvAccount,
                  connectionStatus: null,
                  entitlement: m.entitlement,
                  awaitingConsent: m.awaitingConsent,
                },
              ])
            )
          );
          return;
        }

        const [chatRes, scanRes] = await Promise.all([
          supabase
            .from('chat_sessions')
            .select('user_id, created_at, risk_level, risk_signals')
            .in('user_id', matchedUserIds)
            .eq('is_deleted', false)
            .or(CLIENT_CHAT_SESSION_TYPES)
            .order('created_at', { ascending: false }),
          supabase
            .from('scan_history')
            .select(
              'user_id, created_at, activity, sleep, equilibrium, metabolism, health, relaxation, heart_rate, sbp, dbp, oxygen_saturation, respiration_rate, stress_index, hr_variability'
            )
            .in('user_id', matchedUserIds)
            .order('created_at', { ascending: false }),
        ]);

        const latestChatByUser = {};
        (chatRes?.data || []).forEach((c) => {
          if (!latestChatByUser[c.user_id]) latestChatByUser[c.user_id] = c;
        });

        const latestScanByUser = {};
        (scanRes?.data || []).forEach((s) => {
          if (!latestScanByUser[s.user_id]) latestScanByUser[s.user_id] = s;
        });

        const map = {};
        matched.forEach((m) => {
          if (!m.userId) {
            map[m.patientId] = {
              linked: false,
              hasMvAccount: m.hasMvAccount,
              connectionStatus: null,
              entitlement: m.entitlement,
              awaitingConsent: m.awaitingConsent,
            };
            return;
          }

          const chat = latestChatByUser[m.userId];
          const scan = latestScanByUser[m.userId];

          map[m.patientId] = {
            linked: true,
            hasMvAccount: true,
            connectionStatus: m.connectionStatus,
            entitlement: m.entitlement,
            awaitingConsent: false,
            riskLevel: chat?.risk_level ?? null,
            riskSignals: Array.isArray(chat?.risk_signals) ? chat.risk_signals : [],
            vitalFlags: getVitalFlagsFromScan(scan),
            wellnessFlags: getWellnessFlagsFromScan(scan),
          };
        });

        setMindvaultMap(map);
      } catch (err) {
        console.error('Error loading Mindvault flags:', err);
        setMindvaultMap({});
      }
    };

    loadMindvaultFlags();
  }, [patientList, userDetails?.id]);

  const handleStatusFilterChange = (value) => {
    setSelectedStatusFilters((prevFilters) => {
      const newFilters = [...prevFilters];
      if (!newFilters.includes(value)) {
        if (value === 'All') {
          return ['All'];
        }
        newFilters.push(value);
      } else {
        newFilters.splice(newFilters.indexOf(value), 1);
      }
      return newFilters;
    });
  };

  const handleGenderFilterChange = (value) => {
    setSelectedGenderFilters((prevFilters) => {
      const newFilters = [...prevFilters];
      if (!newFilters.includes(value)) {
        if (value === 'All') {
          return ['All'];
        }
        newFilters.push(value);
      } else {
        newFilters.splice(newFilters.indexOf(value), 1);
      }
      return newFilters;
    });
  };

  useEffect(() => {
    const sortLists = () => {
      const sortedConsultations = [...consultationList]
        .filter(consultation =>
          new Date(new Date(consultation.createdAt).toUTCString().split(' ').slice(0, 4).join(' ')).toDateString() === date.toDateString()
        )
        .sort((a, b) => (sortOrder === 'Newest' ? new Date(b.createdAt) - new Date(a.createdAt) : new Date(a.createdAt) - new Date(b.createdAt)));

      const sourcePatients =
        isVisualTour
          ? demoPatients
          : userMode === 'Corporates' && viewType === 'Vitals'
          ? corporateVitalsRecords
          : patientList;

      const sortedPatients = [...sourcePatients].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setSortedConsultations(sortedConsultations);
      setSortedPatients(sortedPatients);
      setFilteredItems(view === 'Consultations List' ? sortedConsultations : sortedPatients);
    };

    sortLists();
  }, [sortOrder, consultationList, patientList, corporateVitalsRecords, date, view, userMode, viewType, isVisualTour]);

  const applyFilters = () => {
    let filtered = view === 'Consultations List' ? sortedConsultations : sortedPatients;

    if (searchText) {
      filtered =
        view === 'Consultations List'
          ? filtered.filter((consultation) =>
              patientList
                .find((patient) => patient.id === consultation.patient_id)
                ?.name.toLowerCase()
                .includes(searchText)
            )
          : filtered.filter((patient) => {
              // Corporate Vitals: search by user id/tag
              if (userMode === 'Corporates' && viewType === 'Vitals') {
                const userId = (patient?.profile?.userId || patient?.profile?.userTag || '').toLowerCase();
                return userId.includes(searchText);
              }
              // Other modes: existing name search
              return (patient.name || '').toLowerCase().includes(searchText);
            });
    }

    if (!selectedStatusFilters.includes('All')) {
      filtered = filtered.filter((patient) => selectedStatusFilters.includes(patient.status));
    }

    if (!selectedGenderFilters.includes('All')) {
      filtered = filtered.filter((patient) => selectedGenderFilters.includes(patient.gender));
    }

    if (userMode === 'Corporates' && viewType === 'MindVault') {
      const teamMemberIds = teamMembersData.map(m => m.id);
      filtered = filtered.filter((patient) => {
        const owners = Array.isArray(patient.case_owner) ? patient.case_owner : [];
        const teamIds = Array.isArray(patient.team_ids) ? patient.team_ids : [];
        const inCurrentTeam = teamIds.includes(currentOrg?.id) || patient.teamid === currentOrg?.id;
        // A live MindVault connection (paid booking or active access code +
        // consent) counts as visibility for the connected therapist, in
        // addition to explicit case owners. Consent alone no longer does:
        // it used to be read from shared_therapist_ids client-side, which
        // surfaced clients nobody had paid to connect to.
        // Paid-but-unshared counts too: the therapist is billed for this
        // client, so dropping the row would read as "no connection" — the same
        // confusion the restricted-access label exists to prevent. The row
        // still shows no MindVault data, only that access is restricted.
        const connectedToMe =
          !!corporateMap[patient.id]?.connectionStatus ||
          !!corporateMap[patient.id]?.awaitingConsent;
        if (selectedCaseOwnerFilter === 'ALL') {
          return (owners.some(id => teamMemberIds.includes(id)) || connectedToMe) && inCurrentTeam;
        }
        return (
          (owners.includes(selectedCaseOwnerFilter) ||
            (connectedToMe && selectedCaseOwnerFilter === userDetails?.id)) && inCurrentTeam
        );
      });
    }

    setFilteredItems(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [
    searchText,
    selectedStatusFilters,
    selectedGenderFilters,
    view,
    userMode,
    sortedPatients,
    sortedConsultations,
    mindvaultMap,
    corporateMap,
    selectedCaseOwnerFilter,
    teamMembersData,
  ]);

  const handleSortChange = (event) => {
    setSearchText('');
    setSortOrder(event.target.value);
  };

  const handleViewChange = (value) => {
    setView(value);
    setSearchText('');
    setDate(new Date());
  };

  const handleSearchChange = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);
  };

  const handlePageClick = (pageNumber) => {
    setPage(pageNumber - 1); 
  };

  // check last consultation date for a patient
  const getLastConsultationDate = (patientId) => {
    const consultations = consultationList.filter(consultation => consultation.patient_id === patientId);
    consultations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (consultations.length > 0) {
      return new Date(consultations[0].createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return 'No Consultations';
  };

  // check last consultation complaint
  const getLastConsultationComplaint = (patientId) => {
    const consultations = consultationList.filter(consultation => consultation.patient_id === patientId);
    consultations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (consultations.length > 0) {
      return consultations[0].chiefComplaint !== "Not Available" ? consultations[0].chiefComplaint : "No Complaint";
    }
    return 'No Consultations';
  };

  const therapyPeriod = dateRangeFromPreset(timeRange, customFrom, customTo);
  const therapyStats = clientTherapyStats(therapy.history, therapyPeriod.start, therapyPeriod.end);

  const clearFields = () => {
    setFormSubmitted(false); 
    setFirstName(''); 
    setLastName(''); 
    setPhoneNumber('');  
    setRemarks('');
    setGender('Male'); 
    setStatus('Active');
    setBirthDate(null);
    setCaseOwner([]);
    setHkid('');
    setPreExistingConditions([]);
    setEmergencyContact('');
    setEmergencyContactPerson('');
    setPresentingConcerns('');
    setDiagnosis([]);
    setCountryCode('852');
    setEmail('');
  }

  // The patient list itself uses the live workspace above. The two deeper
  // recording states remain a safe visual simulation until the recorder can
  // accept an in-memory patient without starting any network requests.
  if (tourPatientScreens.includes(tourDemoScreen)) {
    return <TourPatientDemo screen={tourDemoScreen} />;
  }

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
        <Grid size={{xs: 1, md: 0}}>
          <MiniDrawer />
        </Grid>
        <Grid size="grow">
          <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column',  width: '100%', height: '100%' }}>
            <Grid size={12}>
              <div style={{ padding: '24px 10vw' }}>
                <Grid container spacing={2} sx={{ flexDirection: { xs: "column", md: "row"} }} justifyContent="space-between">
                  <Grid size={{xs: 12, md: view === 'Consultations List' ? 8 : 12 }}>
                    <Grid data-tour="patient-workspace" container direction="column">
                      {/* <Grid size="auto" sx={{marginBottom: '20px'}}>
                        <Button
                          sx={{
                            textTransform: 'none',
                            borderBottom: '3px solid #29C0BB',
                            paddingRight: '20px',
                            paddingTop: '8px',
                            color: '#000000',
                            fontWeight: 'bold',
                          }}
                        >
                          {userMode === 'Corporates' ? 'Employees' : 'Clients'}
                        </Button>
                      </Grid> */}
                      {
                        (consultationLoading || (userMode === 'Corporates' && viewType === 'Vitals' && corporateVitalsLoading)) ? 
                        <Grid container spacing={2} sx={{ padding: '50px', height: '50vh' }} justifyContent="center" alignItems="center">
                          <Loader /> 
                        </Grid>
                          :
                          newPatient ? (
                            <NewPatientForm
                              userMode={userMode}
                              setNewPatient={setNewPatient}
                              setFormSubmitted={setFormSubmitted}
                              formSubmitted={formSubmitted}
                              phoneNumber={phoneNumber}
                              setPhoneNumber={setPhoneNumber}
                              email={email}
                              setEmail={setEmail}
                              case_owner={case_owner}
                              setCaseOwner={setCaseOwner}
                              setAlert={setAlert}
                              createPatient={createPatient}
                              firstName={firstName}
                              setFirstName={setFirstName}
                              lastName={lastName}
                              setLastName={setLastName}
                              hkid={hkid}
                              setHkid={setHkid}
                              gender={gender}
                              setGender={setGender}
                              birth_date={birth_date}
                              setBirthDate={setBirthDate}
                              remarks={remarks}
                              setRemarks={setRemarks}
                              preExistingConditions={preExistingConditions}
                              setPreExistingConditions={setPreExistingConditions}
                              emergencyContact={emergencyContact}
                              setEmergencyContact={setEmergencyContact}
                              emergencyContactPerson={emergencyContactPerson}
                              setEmergencyContactPerson={setEmergencyContactPerson}
                              presentingConcerns={presentingConcerns}
                              setPresentingConcerns={setPresentingConcerns}
                              diagnosis={diagnosis}
                              setDiagnosis={setDiagnosis}
                              status={status}
                              setStatus={setStatus}
                              countryCode={countryCode}
                              setCountryCode={setCountryCode}
                              teamMembersData={teamMembersData}
                              therapistUserId={userDetails?.id}
                            />
                          )
                          :
                          userMode !== 'Corporates' ?
                          (
                            <ClinicalView 
                              currentOrg={currentOrg}
                              userDetails={userDetails}
                              setAlert={setAlert}
                              setNewPatient={setNewPatient}
                              clearFields={clearFields}
                              teamMembersData={teamMembersData}
                              setCaseOwner={setCaseOwner}
                              searchText={searchText}
                              handleSearchChange={handleSearchChange}
                              genderMenuAnchorEl={genderMenuAnchorEl}
                              setGenderMenuAnchorEl={setGenderMenuAnchorEl}
                              selectedGenderFilters={selectedGenderFilters}
                              handleGenderFilterChange={handleGenderFilterChange}
                              statusMenuAnchorEl={statusMenuAnchorEl}
                              setStatusMenuAnchorEl={setStatusMenuAnchorEl}
                              selectedStatusFilters={selectedStatusFilters}
                              handleStatusFilterChange={handleStatusFilterChange}
                              filteredItems={filteredItems}
                              page={page}
                              rowsPerPage={rowsPerPage}
                              setPage={setPage}
                              handlePageClick={handlePageClick}
                              mindvaultMap={mindvaultMap}
                              getLastConsultationComplaint={getLastConsultationComplaint}
                              getLastConsultationDate={getLastConsultationDate}
                              isVisualTour={isVisualTour}
                            />
                          )
                        :
                        viewType === 'MindVault' ?
                        (
                          <MentalViewMindVault
                            timeRange={timeRange}
                            setTimeRange={setTimeRange}
                            customFrom={customFrom}
                            setCustomFrom={setCustomFrom}
                            customTo={customTo}
                            setCustomTo={setCustomTo}
                            currentOrg={currentOrg}
                            userDetails={userDetails}
                            setAlert={setAlert}
                            setNewPatient={setNewPatient}
                            clearFields={clearFields}
                            teamMembersData={teamMembersData}
                            setCaseOwner={setCaseOwner}
                            searchText={searchText}
                            handleSearchChange={handleSearchChange}
                            filteredItems={filteredItems}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            setPage={setPage}
                            handlePageClick={handlePageClick}
                            corporateMap={corporateMap}
                            therapyStats={therapyStats}
                            therapyLoading={therapy.loading}
                            therapyError={therapy.error}
                            onRetryTherapy={therapy.retry}
                            getLastConsultationComplaint={getLastConsultationComplaint}
                            selectedCaseOwnerFilter={selectedCaseOwnerFilter}
                            setSelectedCaseOwnerFilter={setSelectedCaseOwnerFilter}
                            currentUserRole={currentUserRole}
                          />
                        )
                        :
                          <CorporateViewVitals
                            timeRange={timeRange}
                            setTimeRange={setTimeRange}
                            customFrom={customFrom}
                            setCustomFrom={setCustomFrom}
                            customTo={customTo}
                            setCustomTo={setCustomTo}
                            currentOrg={currentOrg}
                            userDetails={userDetails}
                            setAlert={setAlert}
                            setNewPatient={setNewPatient}
                            clearFields={clearFields}
                            teamMembersData={teamMembersData}
                            setCaseOwner={setCaseOwner}
                            searchText={searchText}
                            handleSearchChange={handleSearchChange}
                            filteredItems={filteredItems}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            setPage={setPage}
                            handlePageClick={handlePageClick}
                            corporateMap={corporateMap}
                          />
                      }
                    </Grid>
                  </Grid>
                  {
                    view === 'Consultations List' ?
                    (
                      <Grid size={{xs: 12, md: 4}} sx={{display: 'flex', justifyContent:{ xs: 'flex-start', md: 'center' }, alignItems: 'center',}}>
                        <Calendar 
                          onChange={setDate} 
                          value={date}
                          style={{borderRadius: '20px',boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'}}
                        />
                      </Grid>
                    ) : <></>
                  }
                  {
                    openDialog && (
                      <AlertDialog data={{open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog}}/>
                    )
                  }
                </Grid>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {(subscriptionActiveStatus !== false && userDetails && !userDetails.mode) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid #29C0BB',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#E0F7FA',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '36px',
                  color: '#29C0BB',
                }}
              >
                ⚙️
              </Typography>
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#333333',
                marginBottom: '16px',
              }}
            >
              Profile Setup Required
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                marginBottom: '32px',
                lineHeight: 1.6,
              }}
            >
              Your profile setup is incomplete. To continue using the application, please select your User Mode in Profile Settings.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#29C0BB',
                  color: '#FFFFFF',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#259B96',
                  },
                }}
                onClick={() => {
                  navigate('/settings');
                }}
              >
                Go to Settings
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
      {/* Subscription Inactive Overlay */}
      {/* Connected MindVault clients keep Scribe usable without a standalone
          subscription; per-action gates still block work on unconnected
          patients. Only hard-block when there is no connected client at all. */}
      {subscriptionActiveStatus === false &&
        !Object.values(mindvaultMap || {}).some((m) => m?.connectionStatus) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid #FF6B6B',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#FFE6E6',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '36px',
                  color: '#FF6B6B',
                }}
              >
                ⚠️
              </Typography>
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#333333',
                marginBottom: '16px',
              }}
            >
              Subscription Inactive
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                marginBottom: '32px',
                lineHeight: 1.6,
              }}
            >
              Your subscription has expired and access to our platform is currently suspended. 
              To continue using our services and manage your clients, please reactivate your subscription from the settings.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#29C0BB',
                  color: '#FFFFFF',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#259B96',
                  },
                }}
                onClick={() => {
                  // Navigate to settings page - you might need to import useNavigate from react-router-dom
                  navigate('/settings');
                }}
              >
                Subscribe Now
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default MedicalScribe;
